import * as Dialog from "@radix-ui/react-dialog";
import { Command } from "cmdk";
import { Languages, Plus, SunMoon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ALL_NAV_ITEMS } from "../app/navigation";
import { changeLocale } from "../i18n";
import { applyTheme, effectiveTheme, getThemePreference } from "../lib/theme";

/** Command palette frame (doc 06 section 20; ADR 0021 rule 4). Navigation and actions in M0;
 *  entity lookup (CR-102, assets) arrives with M1 search. */
export function CommandPalette({
  open,
  onOpenChange,
  onNewCreative,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onNewCreative: () => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const run = (fn: () => void) => () => {
    onOpenChange(false);
    fn();
  };
  const item =
    "flex cursor-pointer items-center gap-3 rounded-[var(--radius-control)] px-3 py-2.5 text-[14px] text-ink data-[selected=true]:bg-elevated";
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[14vh] z-50 w-[640px] max-w-[92vw] -translate-x-1/2 overflow-hidden rounded-[var(--radius-panel)] border border-line bg-surface shadow-[var(--shadow-card)]"
        >
          <Dialog.Title className="sr-only">{t("shell.search_label")}</Dialog.Title>
          <Command label={t("shell.search_label")} className="flex flex-col">
            <Command.Input
              autoFocus
              placeholder={t("palette.placeholder")}
              className="h-14 border-b border-line bg-transparent px-5 text-[16px] text-ink outline-none placeholder:text-ink-3"
            />
            <Command.List className="max-h-[50vh] overflow-y-auto p-2">
              <Command.Empty className="px-3 py-6 text-center text-[14px] text-ink-3">
                {t("palette.empty")}
              </Command.Empty>
              <Command.Group heading={t("palette.group_actions")} className="text-[12px] text-ink-3 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2">
                <Command.Item className={item} onSelect={run(onNewCreative)}>
                  <Plus aria-hidden="true" className="size-4 text-violet" />
                  {t("palette.action_new_creative")}
                </Command.Item>
                <Command.Item
                  className={item}
                  onSelect={run(() => applyTheme(effectiveTheme(getThemePreference()) === "dark" ? "light" : "dark"))}
                >
                  <SunMoon aria-hidden="true" className="size-4 text-ink-2" />
                  {t("palette.action_toggle_theme")}
                </Command.Item>
                <Command.Item
                  className={item}
                  onSelect={run(() => void changeLocale(i18n.language === "ms" ? "en" : "ms"))}
                >
                  <Languages aria-hidden="true" className="size-4 text-ink-2" />
                  {t("palette.action_language")}
                </Command.Item>
              </Command.Group>
              <Command.Group heading={t("palette.group_navigate")} className="text-[12px] text-ink-3 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2">
                {ALL_NAV_ITEMS.map((n) => (
                  <Command.Item key={n.key} className={item} onSelect={run(() => navigate(n.path))}>
                    <n.icon aria-hidden="true" className="size-4 text-ink-2" />
                    {t(`nav.${n.key}`)}
                  </Command.Item>
                ))}
              </Command.Group>
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
